// qbSalesService.js — Analytics v2 (backlog, built overnight 2026-07-11).
// Imports the QB "Sales by Item" report CSV and reconciles QuickBooks sales
// (phone/fax/in-person) against portal orders.
//
// CSV format (confirmed against Brian's real export, 37K rows):
//   BOM'd UTF-8, position-based: Type=5, Date=7, Num=9, Memo=11, Name=13,
//   Qty=15, U/M=17, SalesPrice=19, Amount=21.
//   SKU comes from item SECTION HEADER rows (col 2: "10001 (PMO) ...");
//   detail rows do NOT carry the SKU — the parser tracks the current section.
//   "Total ..." rows and category rows ("Inventory") are skipped.
//   Credit Memos count as negative quantity.
//
// STORAGE: aggregated client-side into ONE Firestore doc per month
// (qbSales/{YYYY-MM}: { month, bySku: { sku: { total, byDay: {date: qty} } } })
// — a full 18-month import is ~19 writes, not 37,000. Re-importing the same
// or an overlapping report REPLACES those month docs, so imports are
// idempotent — no double counting.

import { db } from "../config/firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import Papa from "papaparse";
import { invalidateQbSalesCache } from "./dataCache";

const DETAIL_TYPES = new Set(["Invoice", "Sales Receipt", "Credit Memo"]);

/** Parses the report text. Returns { months, stats } — writes nothing. */
export function parseQbSalesCsv(text) {
  const parsed = Papa.parse(text, { header: false, skipEmptyLines: true });
  const months = {}; // "2025-01" -> { bySku: { sku: { total, byDay } } }
  let currentSku = null;
  let rowsUsed = 0;
  const skus = new Set();
  let firstDate = null;
  let lastDate = null;

  for (const row of parsed.data) {
    const sectionHeader = (row[2] || "").trim();
    if (sectionHeader) {
      // "10001 (PMO) Premium Firm-Tofu ...)" -> sku "10001"; skip "Total ..." rows
      if (/^total\b/i.test(sectionHeader)) { currentSku = null; continue; }
      const m = sectionHeader.match(/^(\S+)\s+\(/);
      currentSku = m ? m[1] : null;
      continue;
    }

    const type = (row[5] || "").trim();
    if (!currentSku || !DETAIL_TYPES.has(type)) continue;

    const date = (row[7] || "").trim(); // "2025-01-02"
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
    let qty = parseFloat((row[15] || "").replace(/,/g, ""));
    if (isNaN(qty)) continue;
    if (type === "Credit Memo" && qty > 0) qty = -qty;

    const month = date.slice(0, 7);
    if (!months[month]) months[month] = { bySku: {} };
    if (!months[month].bySku[currentSku]) months[month].bySku[currentSku] = { total: 0, byDay: {} };
    const bucket = months[month].bySku[currentSku];
    bucket.total = Math.round((bucket.total + qty) * 10000) / 10000;
    bucket.byDay[date] = Math.round(((bucket.byDay[date] || 0) + qty) * 10000) / 10000;

    rowsUsed++;
    skus.add(currentSku);
    if (!firstDate || date < firstDate) firstDate = date;
    if (!lastDate || date > lastDate) lastDate = date;
  }

  return {
    months,
    stats: {
      rowsUsed,
      skuCount: skus.size,
      monthCount: Object.keys(months).length,
      firstDate,
      lastDate
    }
  };
}

/** Writes one doc per month (full replace = idempotent re-imports). */
export async function uploadQbSales(months, adminUid) {
  const keys = Object.keys(months);
  for (const month of keys) {
    await setDoc(doc(db, "qbSales", month), {
      month,
      bySku: months[month].bySku,
      importedAt: serverTimestamp(),
      importedBy: adminUid
    }); // no merge: the report is the truth for that month
  }
  invalidateQbSalesCache();
  return keys.length;
}

/** Sums QB quantities per SKU for [start, end) from cached month docs. */
export function qbQtyBySkuForRange(qbMonthDocs, start, end) {
  const out = {};
  for (const m of qbMonthDocs) {
    for (const [sku, data] of Object.entries(m.bySku || {})) {
      for (const [dateStr, qty] of Object.entries(data.byDay || {})) {
        const [y, mo, d] = dateStr.split("-").map(Number);
        const t = new Date(y, mo - 1, d);
        if (t >= start && t < end) out[sku] = (out[sku] || 0) + qty;
      }
    }
  }
  return out;
}

/** Latest sale date present in the imported QB data (data freshness label). */
export function qbDataThrough(qbMonthDocs) {
  let last = null;
  for (const m of qbMonthDocs) {
    for (const data of Object.values(m.bySku || {})) {
      for (const dateStr of Object.keys(data.byDay || {})) {
        if (!last || dateStr > last) last = dateStr;
      }
    }
  }
  return last;
}
